import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  getDocs,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { usePersonnel } from "../../hooks/usePersonnel";
import { useUnits } from "../../hooks/useUnits";
import { hashString, markPageAsViewed } from "../../utils/employeePages";
import {
  getSeenStatus,
  shouldClearSeenOnEyeClick,
} from "../../utils/validation";

/** Same thresholds as מלאי / עין — must match handleConfirmSeen below */
const EMPLOYEE_PAGE_SEEN_GREEN_MAX = 2;
const EMPLOYEE_PAGE_SEEN_YELLOW_DAYS = 3;
import { useIsMobile } from "../../hooks/useIsMobile";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import { FaEye } from "react-icons/fa";
import "./EmployeeAssignmentPage.css";
import "./MobileEmployeeAssignment.css";
import {
  formatPersonDisplayName,
  findPersonnelById,
  buildNicknameByPersonalNumberMap,
  buildAssigneeSearchHaystack,
  formatAssignedPersonnelList,
  getAssigneeEntriesForEmployeeAssignmentPage,
} from "../../utils/personnelDisplay";
import { normalizeItemSearchText } from "../../utils/itemSearchText";
import {
  applyAppearanceToDom,
  readAppearanceMirror,
} from "../../theme/appearanceStorage";

const EmployeeAssignmentPage = () => {
  const { pageId } = useParams();

  useLayoutEffect(() => {
    const m = readAppearanceMirror();
    if (m === "dark" || m === "light") applyAppearanceToDom(m);
  }, []);
  const { personnel } = usePersonnel();
  const nicknameByPersonalNumber = useMemo(
    () => buildNicknameByPersonalNumberMap(personnel),
    [personnel],
  );
  const { units } = useUnits();
  const isMobile = useIsMobile(); // Mobile detection

  // State
  const [pageData, setPageData] = useState(null);
  const [currentItems, setCurrentItems] = useState([]);
  const [employeeData, setEmployeeData] = useState(null);
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [itemsSearchTerm, setItemsSearchTerm] = useState("");
  const [updatingSeen, setUpdatingSeen] = useState(new Set());

  // Check if page has new changes
  const hasNewChanges = useMemo(() => {
    if (!pageData || !pageData.lastTransaction) return false;

    // If lastViewedAt doesn't exist, there are new changes
    if (!pageData.lastViewedAt) return true;

    // If lastTransactionAt doesn't exist, no new changes
    if (!pageData.lastTransactionAt) return false;

    // Compare timestamps (Firestore timestamps have toMillis() method)
    try {
      const lastTransactionTime = pageData.lastTransactionAt?.toMillis
        ? pageData.lastTransactionAt.toMillis()
        : pageData.lastTransactionAt?.seconds
        ? pageData.lastTransactionAt.seconds * 1000
        : 0;
      const lastViewedTime = pageData.lastViewedAt?.toMillis
        ? pageData.lastViewedAt.toMillis()
        : pageData.lastViewedAt?.seconds
        ? pageData.lastViewedAt.seconds * 1000
        : 0;

      return lastTransactionTime > lastViewedTime;
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error comparing timestamps:", err);
      }
      return false;
    }
  }, [pageData]);

  // Load page data
  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        setError("");

        // 1. Read employeePages document
        const pageDoc = await getDoc(doc(db, "employeePages", pageId));

        if (!pageDoc.exists()) {
          setError("דף לא נמצא");
          setLoading(false);
          return;
        }

        const data = pageDoc.data();

        // Check if page is active
        if (data.isActive === false) {
          setError("דף זה מושבת על ידי המנהל");
          setLoading(false);
          return;
        }

        setPageData(data);

        // 2. Get employee data from personnel collection
        if (data.personnelId) {
          const employee = findPersonnelById(personnel, data.personnelId);
          setEmployeeData(employee);
        }

        // 3. Query inventory for current items
        const inventoryQuery = query(collection(db, "inventory"));
        const inventorySnapshot = await getDocs(inventoryQuery);

        // Filter: items assigned to this employee
        const filteredItems = inventorySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((item) => {
            const firstAssignments = item.assignments?.first || {};
            const secondAssignments = item.assignments?.second || {};
            const personnelId = data.personnelId;

            // Check assignments.first (admin/parentsUser assignments)
            if (firstAssignments[personnelId]) {
              return true;
            }

            // Check assignments.second (childUser assignments)
            if (secondAssignments && typeof secondAssignments === "object") {
              return Object.keys(secondAssignments).some(
                (key) => key === personnelId
              );
            }

            return false;
          });

        setCurrentItems(filteredItems);
        setLoading(false);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error loading page data:", err);
        }
        setError("שגיאה בטעינת הדף");
        setLoading(false);
      }
    };

    if (pageId) {
      loadPageData();
    }
  }, [pageId, personnel]);

  // Helper function: Format date as dd/mm/yy
  const formatDateDDMMYY = useCallback((dateValue) => {
    if (!dateValue) return "-";
    try {
      let date;
      if (dateValue.toDate) {
        // Firestore Timestamp
        date = dateValue.toDate();
      } else if (dateValue.toMillis) {
        // Firestore Timestamp with toMillis
        date = new Date(dateValue.toMillis());
      } else if (dateValue.seconds) {
        // Firestore Timestamp with seconds
        date = new Date(dateValue.seconds * 1000);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        date = new Date(dateValue);
      }

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (e) {
      return "-";
    }
  }, []);

  // Helper function: Get personnel name by ID or email
  const getPersonnelName = useCallback((personnelId, personnelList) => {
    if (!personnelId || !personnelList || personnelList.length === 0) {
      return null;
    }

    const personnelRecord = findPersonnelById(personnelList, personnelId);

    if (personnelRecord) {
      return formatPersonDisplayName(personnelRecord);
    }

    return null;
  }, []);

  // Helper function: Extract assignment data for an item
  const extractAssignmentData = useCallback((item, personnelId) => {
    let assignedQuantity = 0;
    let assignedBy = null;
    let assignedAt = null;
    let note = "";

    const firstAssignment = item.assignments?.first?.[personnelId];
    const secondAssignments = item.assignments?.second;

    if (firstAssignment) {
      assignedQuantity = firstAssignment.quantity || 0;
      assignedBy = firstAssignment.assignedBy;
      assignedAt = firstAssignment.assignedAt;
      note = firstAssignment.note || "";
      // For serial items with no admin note, fall back to child note
      if (!note) {
        const isSerial = item.serialNumber && item.serialNumber.trim() !== "";
        if (isSerial && secondAssignments) {
          const firstChild = Object.values(secondAssignments)[0];
          note = firstChild?.note || "";
        }
      }
    } else if (secondAssignments) {
      const secondAssignment = secondAssignments[personnelId];
      if (secondAssignment) {
        assignedQuantity = secondAssignment.quantity || 1;
        assignedBy = secondAssignment.assignedBy;
        assignedAt = secondAssignment.assignedAt;
        note = secondAssignment.note || "";
      }
    }

    return {
      assignedQuantity,
      assignedBy,
      assignedAt,
      note,
    };
  }, []);

  // Memoize unit name lookup
  const unitName = useMemo(() => {
    if (!employeeData?.unitId || !units?.length) return null;
    const unit = units.find((u) => u.unitId === employeeData.unitId);
    return unit ? unit.unitName : employeeData.unitId;
  }, [employeeData?.unitId, units]);

  // Memoize signedBy name lookup
  const signedByName = useMemo(() => {
    if (!pageData?.lastTransaction) return null;
    const transaction = pageData.lastTransaction;

    // Use signedByName if it exists and is not empty
    if (
      transaction.signedByName &&
      transaction.signedByName.trim() !== "" &&
      !transaction.signedByName.includes("@")
    ) {
      return transaction.signedByName;
    }

    // If signedByName is not available or is an email, look up in personnel
    if (transaction.signedBy) {
      const name = getPersonnelName(transaction.signedBy, personnel);
      if (name) {
        return name;
      }
    }

    // Fallback to signedByName if it exists (even if it's an email)
    if (transaction.signedByName) {
      return transaction.signedByName;
    }

    // Last fallback to signedBy
    return transaction.signedBy || "לא זמין";
  }, [pageData?.lastTransaction, personnel, getPersonnelName]);

  // Confirm or clear seen (second click in green window clears — same as מלאי)
  const handleConfirmSeen = useCallback(
    async (itemId, seenAtFromRow) => {
      if (updatingSeen.has(itemId)) return;

      setUpdatingSeen((prev) => new Set(prev).add(itemId));

      try {
        const itemRef = doc(db, "inventory", itemId);
        const now = new Date();
        const shouldClear = shouldClearSeenOnEyeClick(
          seenAtFromRow,
          EMPLOYEE_PAGE_SEEN_GREEN_MAX,
          EMPLOYEE_PAGE_SEEN_YELLOW_DAYS,
        );

        await updateDoc(
          itemRef,
          shouldClear
            ? { seenAt: deleteField(), updatedAt: now }
            : { seenAt: now, updatedAt: now }
        );

        setCurrentItems((prev) =>
          prev.map((item) => {
            if (item.id !== itemId) return item;
            if (shouldClear) {
              const { seenAt: _removed, ...rest } = item;
              return { ...rest, updatedAt: now };
            }
            return { ...item, seenAt: now, updatedAt: now };
          })
        );
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error updating seenAt:", err);
        }
        alert("שגיאה בעדכון סטטוס נראה");
      } finally {
        setUpdatingSeen((prev) => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      }
    },
    [updatingSeen]
  );

  // Process items data (shared for both desktop and mobile)
  const processedItems = useMemo(() => {
    // Filter current items based on search term
    const filteredItems = currentItems.filter((item) => {
      if (!itemsSearchTerm.trim()) return true;
      const searchTerm = normalizeItemSearchText(itemsSearchTerm.trim());
      const itemName = normalizeItemSearchText(item.itemName);
      const serialNumber = normalizeItemSearchText(item.serialNumber);
      const { assignedBy } = extractAssignmentData(item, pageData?.personnelId);
      const assignedByName = normalizeItemSearchText(
        getPersonnelName(assignedBy, personnel) || assignedBy || "",
      );
      const assignerRecord = assignedBy
        ? findPersonnelById(personnel, assignedBy)
        : null;
      const assignerNick = normalizeItemSearchText(
        assignerRecord?.nickname || "",
      ).trim();
      const assignerLegal = normalizeItemSearchText(
        [assignerRecord?.firstName, assignerRecord?.lastName]
          .filter(Boolean)
          .join(" "),
      ).trim();
      const assignerHaystack = [assignedByName, assignerNick, assignerLegal]
        .filter(Boolean)
        .join(" ");
      const assigneeEntries = getAssigneeEntriesForEmployeeAssignmentPage(
        item,
        pageData?.personnelId,
      );
      const assigneeHaystack = buildAssigneeSearchHaystack(
        assigneeEntries,
        nicknameByPersonalNumber,
        personnel,
      );
      return (
        itemName.includes(searchTerm) ||
        serialNumber.includes(searchTerm) ||
        (assignerHaystack && assignerHaystack.includes(searchTerm)) ||
        assigneeHaystack.includes(searchTerm)
      );
    });

    // Process items with assignment data
    return filteredItems.map((item) => {
      // Extract assignment data using helper function
      const { assignedQuantity, assignedAt, note } = extractAssignmentData(
        item,
        pageData?.personnelId,
      );

      // Format assignedAt using helper function
      const assignedAtFormatted = formatDateDDMMYY(assignedAt);

      const isSerial = item.serialNumber && item.serialNumber.trim() !== "";
      const status = item.status || "available";
      const isOrganic = item.isOrganic;
      const seenStatus = getSeenStatus(
        item.seenAt,
        EMPLOYEE_PAGE_SEEN_GREEN_MAX,
        EMPLOYEE_PAGE_SEEN_YELLOW_DAYS,
      );

      const assigneeEntries = getAssigneeEntriesForEmployeeAssignmentPage(
        item,
        pageData?.personnelId,
      );
      const assignedToName =
        formatAssignedPersonnelList(
          assigneeEntries,
          nicknameByPersonalNumber,
          personnel,
        ) || "-";

      // Build "Name (Unit)" for mobile display
      const assignedToNameWithUnit = (() => {
        if (!assigneeEntries || assigneeEntries.length === 0) return assignedToName;
        const parts = assigneeEntries.map((entry) => {
          const name = formatAssignedPersonnelList(
            [entry],
            nicknameByPersonalNumber,
            personnel,
          );
          const person = personnel.find(
            (p) => String(p.personalNumber).trim() === String(entry.personalNumber).trim()
          );
          const unitId = person?.unitId;
          const unit = unitId
            ? units.find((u) => u.id === unitId)
            : null;
          const unitName = unit?.unitName || "";
          return unitName ? `${name} (${unitName})` : name;
        });
        return parts.join(", ") || "-";
      })();

      return {
        ...item,
        assignedQuantity,
        assignedToName,
        assignedToNameWithUnit,
        assignedAtFormatted,
        isSerial,
        status,
        isOrganic,
        note,
        seenStatus,
      };
    });
  }, [
    currentItems,
    itemsSearchTerm,
    pageData?.personnelId,
    personnel,
    nicknameByPersonalNumber,
    extractAssignmentData,
    getPersonnelName,
    formatDateDDMMYY,
    units,
  ]);

  // Memoize desktop table
  const processedItemsTable = useMemo(() => {
    if (currentItems.length === 0) {
      return <p className="no-items">אין פריטים מוחתמים כרגע</p>;
    }

    if (processedItems.length === 0) {
      return (
        <p className="no-items">
          לא נמצאו פריטים התואמים לחיפוש "{itemsSearchTerm}"
        </p>
      );
    }

    return (
      <table className="items-table">
        <thead>
          <tr>
            <th>פריט</th>
            <th>חתום ל</th>
            <th>כמות</th>
            <th>סוג</th>
            <th>תאריך הקצאה</th>
            <th>מיקום/הערה</th>
            <th>דוח צ</th>
          </tr>
        </thead>
        <tbody>
          {processedItems.map((item) => (
            <tr key={item.id} className={`item-row ${item.status}`}>
              <td>
                <div className="employee-item-name-line">
                  <span className="employee-item-name">{item.itemName}</span>
                  {item.isSerial && item.serialNumber?.trim() && (
                    <span className="employee-item-serial-inline">
                      · {item.serialNumber.trim()}
                    </span>
                  )}
                </div>
              </td>
              <td>{item.assignedToName}</td>
              <td>
                {item.isSerial
                  ? item.assignedQuantity > 0
                    ? "1"
                    : "0"
                  : item.assignedQuantity}
              </td>
              <td>
                <span
                  className={`item-type ${
                    item.isOrganic ? "organic" : "synthetic"
                  }`}
                >
                  {item.isOrganic ? "אורגני" : "קווי"}
                </span>
              </td>
              <td>{item.assignedAtFormatted}</td>
              <td>{item.note || "-"}</td>
              <td className="seen-action-cell">
                {item.isSerial ? (
                  <div className="seen-action-wrap">
                    <div
                      className={`seen-cell ${
                        item.seenStatus?.colorClass || "seen-no-data"
                      }`}
                      aria-hidden
                    >
                      <div className="seen-days">
                        {item.seenStatus?.days != null
                          ? item.seenStatus.days
                          : "-"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleConfirmSeen(item.id, item.seenAt)}
                      className="seen-btn"
                      title={
                        shouldClearSeenOnEyeClick(
                          item.seenAt,
                          EMPLOYEE_PAGE_SEEN_GREEN_MAX,
                          EMPLOYEE_PAGE_SEEN_YELLOW_DAYS,
                        )
                          ? "בטל סימון נראה (בטעות)"
                          : "אשר נראה"
                      }
                      disabled={updatingSeen.has(item.id)}
                    >
                      <FaEye />
                    </button>
                  </div>
                ) : (
                  ""
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }, [
    processedItems,
    currentItems.length,
    itemsSearchTerm,
    handleConfirmSeen,
    updatingSeen,
  ]);

  // Memoize mobile cards
  const processedItemsCards = useMemo(() => {
    if (currentItems.length === 0) {
      return <p className="no-items">אין פריטים מוחתמים כרגע</p>;
    }

    if (processedItems.length === 0) {
      return (
        <p className="no-items">
          לא נמצאו פריטים התואמים לחיפוש "{itemsSearchTerm}"
        </p>
      );
    }

    return (
      <div className="mobile-items-cards">
        {processedItems.map((item) => (
          <div key={item.id} className="mobile-item-card">
            <div className="mobile-item-card-header">
              <h4 className="mobile-item-card-title">
                <span className="mobile-item-card-name">{item.itemName}</span>
                {item.isSerial && item.serialNumber?.trim() && (
                  <span className="mobile-item-card-serial-inline">
                    {" "}
                    · {item.serialNumber.trim()}
                  </span>
                )}
              </h4>
            </div>
            <div className="mobile-item-card-content">
              <div className="mobile-item-card-row">
                <span className="mobile-item-card-label">חתום ל:</span>
                <span className="mobile-item-card-value">
                  {item.assignedToNameWithUnit}
                </span>
              </div>
              <div className="mobile-item-card-row">
                <span className="mobile-item-card-label">כמות:</span>
                <span className="mobile-item-card-value">
                  {item.isSerial
                    ? item.assignedQuantity > 0
                      ? "1"
                      : "0"
                    : item.assignedQuantity}
                </span>
              </div>
              <div className="mobile-item-card-row">
                <span className="mobile-item-card-label">סוג:</span>
                <span
                  className={`item-type ${
                    item.isOrganic ? "organic" : "synthetic"
                  }`}
                >
                  {item.isOrganic ? "אורגני" : "קווי"}
                </span>
              </div>
              <div className="mobile-item-card-row">
                <span className="mobile-item-card-label">תאריך הקצאה:</span>
                <span className="mobile-item-card-value">
                  {item.assignedAtFormatted}
                </span>
              </div>
              {item.note && (
                <div className="mobile-item-card-row">
                  <span className="mobile-item-card-label">מיקום/הערה:</span>
                  <span className="mobile-item-card-value">{item.note}</span>
                </div>
              )}
            </div>
            {item.isSerial && (
              <div className="mobile-item-card-actions">
                <div
                  className={`seen-cell ${
                    item.seenStatus?.colorClass || "seen-no-data"
                  }`}
                  aria-hidden
                >
                  <div className="seen-days">
                    {item.seenStatus?.days != null
                      ? item.seenStatus.days
                      : "-"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleConfirmSeen(item.id, item.seenAt)}
                  className="mobile-seen-btn"
                  title={
                    shouldClearSeenOnEyeClick(
                      item.seenAt,
                      EMPLOYEE_PAGE_SEEN_GREEN_MAX,
                      EMPLOYEE_PAGE_SEEN_YELLOW_DAYS,
                    )
                      ? "בטל סימון נראה (בטעות)"
                      : "אשר נראה"
                  }
                  disabled={updatingSeen.has(item.id)}
                >
                  <FaEye />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }, [
    processedItems,
    currentItems.length,
    itemsSearchTerm,
    handleConfirmSeen,
    updatingSeen,
  ]);

  // Handle password verification
  const handlePasswordSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setPasswordError("");

      if (!password || !password.trim()) {
        setPasswordError("נא להזין מספר אישי");
        return;
      }

      try {
        const storedHash = pageData?.passwordHash;

        if (!storedHash) {
          setPasswordError("שגיאה: לא נמצא hash סיסמה");
          return;
        }

        // Try multiple normalization approaches to handle different input formats
        // 1. Normalized (remove all non-numeric)
        const normalizedPassword = password
          .replace(/\s+/g, "") // Remove all whitespace
          .replace(/[^\d]/g, "") // Remove all non-numeric characters
          .trim();

        // 2. Just trimmed (in case stored hash was created with spaces)
        const trimmedPassword = password.trim();

        // 3. Try as string (in case it's stored as number)
        const stringPassword = String(password).trim();

        // Try all variations
        let enteredHash = null;
        let matched = false;

        // Try normalized first (most common case)
        if (normalizedPassword) {
          enteredHash = await hashString(normalizedPassword);
          if (enteredHash === storedHash) {
            matched = true;
          }
        }

        // If normalized didn't match, try trimmed
        if (
          !matched &&
          trimmedPassword &&
          trimmedPassword !== normalizedPassword
        ) {
          enteredHash = await hashString(trimmedPassword);
          if (enteredHash === storedHash) {
            matched = true;
          }
        }

        // If still no match, try string conversion
        if (!matched && stringPassword && stringPassword !== trimmedPassword) {
          enteredHash = await hashString(stringPassword);
          if (enteredHash === storedHash) {
            matched = true;
          }
        }

        if (matched) {
          setIsAuthenticated(true);
          setPasswordError("");

          // Mark page as viewed (always mark, not just if has new changes)
          try {
            await markPageAsViewed(pageId);
            // Update local state to reflect view
            setPageData((prev) => ({
              ...prev,
              lastViewedAt: new Date(),
            }));
          } catch (err) {
            if (process.env.NODE_ENV === "development") {
              console.error("Error marking page as viewed:", err);
            }
          }
        } else {
          setPasswordError("מספר אישי שגוי");
        }
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error verifying password:", err);
        }
        setPasswordError("שגיאה באימות הסיסמה");
      }
    },
    [password, pageData, pageId]
  );

  // Format timestamp
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return "לא זמין";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString("he-IL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (err) {
      return "לא זמין";
    }
  }, []);

  if (loading) {
    return (
      <div className="employee-assignment-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="employee-assignment-page">
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="employee-assignment-page">
        <div className="password-container">
          <h2>הזנת מספר אישי</h2>
          <p>יש להזין את המספר האישי לצפייה בפרטי הציוד</p>
          <form onSubmit={handlePasswordSubmit} className="password-form">
            <div className="form-group">
              <label htmlFor="password">מספר אישי:</label>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                id="password"
                value={password}
                onChange={(e) => {
                  // Only allow numeric input, remove any non-numeric characters
                  const value = e.target.value.replace(/[^\d]/g, "");
                  setPassword(value);
                }}
                onFocus={(e) => {
                  // Select all text when input is focused
                  e.target.select();
                }}
                onBlur={(e) => {
                  // Ensure value is trimmed on blur
                  const trimmed = e.target.value.trim();
                  if (trimmed !== password) {
                    setPassword(trimmed);
                  }
                }}
                className={passwordError ? "error-input" : ""}
                placeholder="הזן מספר אישי"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              {passwordError && (
                <div className="error-message">{passwordError}</div>
              )}
            </div>
            <button type="submit" className="submit-btn">
              המשך
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-assignment-page">
      <div className="page-header">
        {/* <h1>פרטי ציוד מוחתם</h1> */}
        {employeeData && (
          <div className="employee-info">
            <h2>{formatPersonDisplayName(employeeData)}</h2>
            <p>מספר אישי: {employeeData.personalNumber}</p>
            {employeeData.unitId && unitName && <p>יחידה: {unitName}</p>}
          </div>
        )}
      </div>

      {/* New Changes Section */}
      {hasNewChanges && pageData.lastTransaction && (
        <div className="new-changes-section">
          <h3 className="new-changes-title">
            שינויים אחרונים <span className="new-badge">חדש</span>
          </h3>
          <div className="transaction-info">
            <p>
              <strong>הוקצה על ידי:</strong> {signedByName || "לא זמין"}
            </p>
            <p>
              <strong>תאריך:</strong>{" "}
              {formatTimestamp(pageData.lastTransaction.signedAt)}
            </p>
          </div>

          {/* Transaction Items */}
          <div className="transaction-items">
            {/* Desktop: Table wrapper with title */}
            <div className="transaction-items-table-wrapper">
              <h4>פריטים שעברו שינוי:</h4>
              {/* Desktop Table */}
              <table className="items-table">
                <thead>
                  <tr>
                    <th>פריט</th>
                    <th>מספר סידורי</th>
                    <th>כמות</th>
                    <th>פעולה</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.lastTransaction.items.map((item, index) => (
                    <tr
                      key={index}
                      className={
                        item.action === "removed" ? "removed-item" : ""
                      }
                    >
                      <td>{item.itemName}</td>
                      <td>{item.serial || "-"}</td>
                      <td>{Math.abs(item.quantity)}</td>
                      <td>
                        <span
                          className={`action-badge ${
                            item.action === "added" ? "added" : "removed"
                          }`}
                        >
                          {item.action === "added" ? "נוסף" : "הוסר"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile Cards */}
            {isMobile && (
              <div className="mobile-transaction-items">
                {pageData.lastTransaction.items.map((item, index) => (
                  <div
                    key={index}
                    className={`mobile-transaction-item-card ${
                      item.action === "removed" ? "removed" : ""
                    }`}
                  >
                    <div className="mobile-transaction-item-row">
                      <span className="mobile-transaction-item-label">
                        פריט:
                      </span>
                      <span className="mobile-transaction-item-value">
                        {item.itemName}
                      </span>
                    </div>
                    {item.serial && (
                      <div className="mobile-transaction-item-row">
                        <span className="mobile-transaction-item-label">
                          מספר סידורי:
                        </span>
                        <span className="mobile-transaction-item-value">
                          {item.serial}
                        </span>
                      </div>
                    )}
                    <div className="mobile-transaction-item-row">
                      <span className="mobile-transaction-item-label">
                        כמות:
                      </span>
                      <span className="mobile-transaction-item-value">
                        {Math.abs(item.quantity)}
                      </span>
                    </div>
                    <div className="mobile-transaction-item-action">
                      <span
                        className={`action-badge ${
                          item.action === "added" ? "added" : "removed"
                        }`}
                      >
                        {item.action === "added" ? "נוסף" : "הוסר"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Signature */}
            {pageData.lastTransaction.signatureDataUrl && (
              <div className="signature-section">
                <h4>חתימה:</h4>
                <img
                  src={pageData.lastTransaction.signatureDataUrl}
                  alt="חתימה"
                  className="signature-image"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current Items Section */}
      <div className="current-items-section">
        <div className="current-items-header">
          <h3>ציוד קיים</h3>
          {currentItems.length > 0 && (
            <div className="items-search-container">
              <input
                type="text"
                placeholder="חפש לפי שם פריט, מספר סידורי, חתום ל או כינוי..."
                value={itemsSearchTerm}
                onChange={(e) => setItemsSearchTerm(e.target.value)}
                className="items-search-input"
              />
            </div>
          )}
        </div>
        {isMobile ? processedItemsCards : processedItemsTable}
      </div>
    </div>
  );
};

export default EmployeeAssignmentPage;
